export function tbToChartData(
  data: Record<string, { ts: number; value: string }[]>,
  key: string,
  range: string,
  fallback: any[],
  conversionFactor: number = 1
) {
  if (range === 'realtime') {
    if (!data || !data[key] || data[key].length === 0) return fallback;
    const arr = [...data[key]].sort((a, b) => a.ts - b.ts);
    return arr.map(d => {
      const date = new Date(d.ts);
      return {
        name: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ts: d.ts,
        value: Number(((parseFloat(d.value) || 0) * conversionFactor).toFixed(2))
      };
    });
  }

  const timeMap = new Map<string, any>();
  const now = new Date();
  
  if (range === 'daily') {
    now.setMinutes(0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 3600000);
      const name = `${d.getHours()}시`;
      timeMap.set(name, { name, value: 0, ts: d.getTime() });
    }
  } else if (range === 'weekly' || range === 'monthly') {
    now.setHours(0, 0, 0, 0);
    const count = range === 'weekly' ? 7 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const name = range === 'weekly' 
        ? ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
        : `${d.getDate()}일`;
      timeMap.set(name, { name, value: 0, ts: d.getTime() });
    }
  } else {
    return fallback;
  }

  if (data && data[key]) {
    for (const d of data[key]) {
      const date = new Date(d.ts);
      let name = '';
      if (range === 'daily') name = `${date.getHours()}시`;
      else if (range === 'weekly') name = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      else if (range === 'monthly') name = `${date.getDate()}일`;
      
      if (timeMap.has(name)) {
        timeMap.get(name).value = Number(((parseFloat(d.value) || 0) * conversionFactor).toFixed(2));
      }
    }
  }

  return Array.from(timeMap.values());
}

export function tbToCombinedChartData(
  data: Record<string, { ts: number; value: string }[]>,
  key1: string,
  key1OutputKey: string,
  key2: string,
  key2OutputKey: string,
  range: string,
  fallback: any[],
  conversionFactor1: number = 1,
  conversionFactor2: number = 1
) {
  if (range === 'realtime') {
    if (!data || (!data[key1] && !data[key2])) return fallback;
    const timeMap = new Map<number, any>();
    const processRealtime = (arr: { ts: number; value: string }[] | undefined, outKey: string, factor: number) => {
      if (!arr) return;
      const sorted = [...arr].sort((a, b) => a.ts - b.ts);
      for (const d of sorted) {
        // Group points into 5-second buckets so simultaneous key reports pair together seamlessly
        const bucketTs = Math.round(d.ts / 5000) * 5000;
        const date = new Date(bucketTs);
        const name = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (!timeMap.has(bucketTs)) {
          timeMap.set(bucketTs, {
            name,
            ts: bucketTs,
            [key1OutputKey]: null,
            [key2OutputKey]: null
          });
        }
        const entry = timeMap.get(bucketTs);
        entry[outKey] = Number(((parseFloat(d.value) || 0) * factor).toFixed(2));
      }
    };
    processRealtime(data[key1], key1OutputKey, conversionFactor1);
    processRealtime(data[key2], key2OutputKey, conversionFactor2);
    
    if (timeMap.size === 0) return fallback;
    const result = Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
    
    let last1: number | null = null;
    let last2: number | null = null;
    for (const r of result) {
      if (r[key1OutputKey] !== null) last1 = r[key1OutputKey]; else r[key1OutputKey] = last1;
      if (r[key2OutputKey] !== null) last2 = r[key2OutputKey]; else r[key2OutputKey] = last2;
    }
    return result;
  }

  const timeMap = new Map<string, any>();
  const now = new Date();
  
  if (range === 'daily') {
    now.setMinutes(0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 3600000);
      const name = `${d.getHours()}시`;
      timeMap.set(name, { name, ts: d.getTime(), [key1OutputKey]: null, [key2OutputKey]: null });
    }
  } else if (range === 'weekly' || range === 'monthly') {
    now.setHours(0, 0, 0, 0);
    const count = range === 'weekly' ? 7 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const name = range === 'weekly' 
        ? ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
        : `${d.getDate()}일`;
      timeMap.set(name, { name, ts: d.getTime(), [key1OutputKey]: null, [key2OutputKey]: null });
    }
  } else {
    return fallback;
  }

  const processArray = (arr: { ts: number; value: string }[] | undefined, outKey: string, factor: number) => {
    if (!arr) return;
    for (const d of arr) {
      const date = new Date(d.ts);
      let name = '';
      if (range === 'daily') name = `${date.getHours()}시`;
      else if (range === 'weekly') name = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      else if (range === 'monthly') name = `${date.getDate()}일`;
      
      if (timeMap.has(name)) {
        timeMap.get(name)[outKey] = Number(((parseFloat(d.value) || 0) * factor).toFixed(2));
      }
    }
  };

  processArray(data?.[key1], key1OutputKey, conversionFactor1);
  processArray(data?.[key2], key2OutputKey, conversionFactor2);

  const result = Array.from(timeMap.values());
  
  let last1: number | null = null;
  let last2: number | null = null;
  for (const r of result) {
    if (r[key1OutputKey] !== null) last1 = r[key1OutputKey]; else r[key1OutputKey] = last1;
    if (r[key2OutputKey] !== null) last2 = r[key2OutputKey]; else r[key2OutputKey] = last2;
  }

  return result;
}

export function tbToErvCo2ChartData(
  ervData: Record<string, { ts: number; value: any }[]> | undefined,
  co2Data: Record<string, { ts: number; value: any }[]> | undefined,
  range: string,
  fallback: any[] = []
) {
  const ervRaw = (ervData?.['erv'] || ervData?.['ERV'] || ervData?.['speed'] || []).slice().sort((a, b) => a.ts - b.ts);
  const co2Raw = (co2Data?.['CO2'] || co2Data?.['co2'] || []).slice().sort((a, b) => a.ts - b.ts);

  if (range === 'realtime') {
    if (ervRaw.length === 0 && co2Raw.length === 0) {
      return fallback;
    }
    const allTs = new Set<number>();
    ervRaw.forEach(d => allTs.add(d.ts));
    co2Raw.forEach(d => allTs.add(d.ts));

    const sortedTs = Array.from(allTs).sort((a, b) => a - b);
    const ervMap = new Map<number, number>();
    ervRaw.forEach(d => {
      let v = Number(d.value);
      if (isNaN(v) || v < 0) v = 0;
      ervMap.set(d.ts, Math.max(0, Math.min(3, v)));
    });

    const co2Map = new Map<number, number>();
    co2Raw.forEach(d => {
      const v = parseFloat(d.value);
      if (!isNaN(v)) co2Map.set(d.ts, Math.round(v));
    });

    let lastErv = ervRaw.length > 0 ? (Number(ervRaw[0].value) > 0 ? Number(ervRaw[0].value) : 0) : 0;
    let lastCo2 = co2Raw.length > 0 ? Math.round(parseFloat(co2Raw[0].value) || 450) : 450;

    const result = sortedTs.map((ts) => {
      if (ervMap.has(ts)) lastErv = ervMap.get(ts)!;
      if (co2Map.has(ts)) lastCo2 = co2Map.get(ts)!;

      const date = new Date(ts);
      const name = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return { name, ts, erv: lastErv, co2: lastCo2 };
    });

    return result.length > 0 ? result : fallback;
  }

  // Daily / Weekly / Monthly Range
  const buckets: { name: string; startTs: number; endTs: number; erv: number | null; co2: number | null }[] = [];
  const now = new Date();

  if (range === 'daily') {
    now.setMinutes(0, 0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 3600000);
      const name = `${d.getHours()}시`;
      buckets.push({ name, startTs: d.getTime(), endTs: d.getTime() + 3600000, erv: null, co2: null });
    }
  } else if (range === 'weekly' || range === 'monthly') {
    now.setHours(0, 0, 0, 0);
    const count = range === 'weekly' ? 7 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const name = range === 'weekly'
        ? ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
        : `${d.getDate()}일`;
      buckets.push({ name, startTs: d.getTime(), endTs: d.getTime() + 86400000, erv: null, co2: null });
    }
  } else {
    return fallback;
  }

  // Populate CO2 averages per bucket and ERV latest state per bucket
  for (const b of buckets) {
    const bCo2 = co2Raw.filter(d => d.ts >= b.startTs && d.ts < b.endTs);
    if (bCo2.length > 0) {
      const sum = bCo2.reduce((acc, c) => acc + (parseFloat(c.value) || 0), 0);
      b.co2 = Math.round(sum / bCo2.length);
    }
    const bErv = ervRaw.filter(d => d.ts >= b.startTs && d.ts < b.endTs);
    if (bErv.length > 0) {
      const last = bErv[bErv.length - 1];
      let v = Number(last.value);
      if (isNaN(v) || v < 0) v = 0;
      b.erv = Math.max(0, Math.min(3, v));
    }
  }

  // Forward fill across buckets so state is held continuously
  let runningErv = 0;
  const priorErv = ervRaw.filter(d => d.ts < buckets[0].startTs);
  if (priorErv.length > 0) {
    const pv = Number(priorErv[priorErv.length - 1].value);
    if (!isNaN(pv) && pv > 0) runningErv = Math.max(0, Math.min(3, pv));
  }

  let runningCo2 = 450;
  const priorCo2 = co2Raw.filter(d => d.ts < buckets[0].startTs);
  if (priorCo2.length > 0) {
    const pc = parseFloat(priorCo2[priorCo2.length - 1].value);
    if (!isNaN(pc)) runningCo2 = Math.round(pc);
  }

  const result = buckets.map(b => {
    if (b.erv !== null) runningErv = b.erv;
    if (b.co2 !== null) runningCo2 = b.co2;
    return {
      name: b.name,
      ts: b.startTs,
      erv: runningErv,
      co2: runningCo2
    };
  });

  return result;
}


